
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { z } from "zod";

const settingsSchema = z.object({
  content_interval_ms: z.number().min(1000, "Minimum 1 second").max(60000, "Maximum 60 seconds"),
  carousel_interval_ms: z.number().min(1000, "Minimum 1 second").max(30000, "Maximum 30 seconds")
});

type SettingsFormData = z.infer<typeof settingsSchema>;

const HeroSettingsPanel = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['hero-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hero_settings')
        .select('*')
        .eq('id', 'main')
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      content_interval_ms: 8000,
      carousel_interval_ms: 5000
    },
    values: settings ? {
      content_interval_ms: settings.content_interval_ms,
      carousel_interval_ms: settings.carousel_interval_ms
    } : undefined
  });

  const mutation = useMutation({
    mutationFn: async (data: SettingsFormData) => {
      const { error } = await supabase
        .from('hero_settings')
        .update(data)
        .eq('id', 'main');
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hero-settings'] });
      queryClient.invalidateQueries({ queryKey: ['heroData'] });
      toast.success('Settings updated');
    },
    onError: () => {
      toast.error('Failed to update settings');
    }
  });

  const onSubmit = (data: SettingsFormData) => {
    mutation.mutate(data);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Hero Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-20 bg-muted rounded animate-pulse" />
            <div className="h-20 bg-muted rounded animate-pulse" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hero Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="content_interval_ms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content Rotation Interval</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1000"
                        max="60000"
                        step="1000"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 8000)}
                        className="max-w-32"
                      />
                      <span className="text-sm text-muted-foreground">milliseconds</span>
                      <span className="text-sm text-muted-foreground">
                        ({((field.value || 8000) / 1000).toFixed(1)}s)
                      </span>
                    </div>
                  </FormControl>
                  <FormDescription>
                    How long each hero content slide is displayed before rotating to the next
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="carousel_interval_ms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Carousel Rotation Interval</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1000"
                        max="30000"
                        step="1000"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 5000)}
                        className="max-w-32"
                      />
                      <span className="text-sm text-muted-foreground">milliseconds</span>
                      <span className="text-sm text-muted-foreground">
                        ({((field.value || 5000) / 1000).toFixed(1)}s)
                      </span>
                    </div>
                  </FormControl>
                  <FormDescription>
                    How long each featured product is displayed in the carousel before rotating
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button type="submit" loading={mutation.isPending}>
                Save Settings
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default HeroSettingsPanel;
